import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiState } from '../../models/generics';
import { TGhostUser, TPanemUser } from '../../services/meta/models/meta';
import { TGetAddressesApiResponseData } from '../../services/user/models/addresses';
import { ProfileMenuApiResponseData } from '../../services/user/models/profile-menu.types';

// Define the state interface
export interface TUserState {
    getUserApiState: ApiState;
    getAddressessApiState: ApiState;
    isLoggedIn: boolean;
    loadingMeta: boolean;
    isMetaLoaded: boolean;
    ghostUser?: TGhostUser;
    user?: TPanemUser;
    loadingProfileMenu: boolean;
    profileMenu?: ProfileMenuApiResponseData;
    errorLoadingProfileMenu: boolean;
    loggingOut: boolean;
    loggingOutFailed: boolean;
    loggingOutSuccess: boolean;
    addresses?: TGetAddressesApiResponseData;
    creatingAddress: boolean;
}

// Define the initial state
const initialState: TUserState = {
    getUserApiState: ApiState.IDLE,
    getAddressessApiState: ApiState.IDLE,
    isLoggedIn: false,
    loadingMeta: true,
    isMetaLoaded: false,
    ghostUser: undefined,
    user: undefined,
    loadingProfileMenu: true,
    profileMenu: undefined,
    errorLoadingProfileMenu: false,
    loggingOut: false,
    loggingOutFailed: false,
    loggingOutSuccess: false,
    addresses: undefined,
    creatingAddress: false,
};

// Create the user slice
const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        // Get Meta actions
        getMetaLoading: (state) => {
            state.getUserApiState = ApiState.LOADING;
        },
        getMetaSuccess: (state, action: PayloadAction<{ user?: TPanemUser, ghost_user?: TGhostUser }>) => {
            state.getUserApiState = ApiState.SUCCESS;
            state.isMetaLoaded = true;
            state.isLoggedIn = !!action.payload?.user;
            state.ghostUser = action.payload?.ghost_user;
            state.user = action.payload?.user;
        },
        getMetaFailed: (state) => {
            state.getUserApiState = ApiState.FAILED;
        },

        // Profile Menu actions
        getProfileMenuLoading: (state) => {
            state.loadingProfileMenu = true;
            state.errorLoadingProfileMenu = false;
        },
        getProfileMenuSuccess: (state, action: PayloadAction<ProfileMenuApiResponseData>) => {
            state.loadingProfileMenu = false;
            state.profileMenu = action.payload;
        },
        getProfileMenuFailed: (state) => {
            state.loadingProfileMenu = false;
            state.errorLoadingProfileMenu = true;
        },

        // Logout actions
        logoutLoading: (state) => {
            state.loggingOut = true;
        },
        logoutSuccess: (state, action: PayloadAction<TGhostUser | undefined>) => {
            state.loggingOut = false;
            state.loggingOutSuccess = true;
            state.ghostUser = action.payload;
            state.isLoggedIn = false;
            state.user = undefined;
        },
        logoutFailed: (state) => {
            state.loggingOut = false;
            state.loggingOutFailed = true;
        },

        // Address actions
        getAddressesLoading: (state) => {
            state.getAddressessApiState = ApiState.LOADING;
        },
        getAddressesSuccess: (state, action: PayloadAction<TGetAddressesApiResponseData>) => {
            state.getAddressessApiState = ApiState.SUCCESS;
            state.addresses = action.payload;
        },
        getAddressesFailed: (state) => {
            state.getAddressessApiState = ApiState.FAILED;
        },

        // Create Address actions
        createAddressLoading: (state) => {
            state.creatingAddress = true;
        },
        createAddressSuccess: (state) => {
            state.creatingAddress = false;
        },
        createAddressFailed: (state) => {
            state.creatingAddress = false;
        },

       
    }
});

// Export actions
export const {
    getMetaLoading,
    getMetaSuccess,
    getMetaFailed,
    getProfileMenuLoading,
    getProfileMenuSuccess,
    getProfileMenuFailed,
    logoutLoading,
    logoutSuccess,
    logoutFailed,
    getAddressesLoading,
    getAddressesSuccess,
    getAddressesFailed,
    createAddressLoading,
    createAddressSuccess,
    createAddressFailed,
    
} = userSlice.actions;

export default userSlice.reducer;